import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvGet, dvCreate, dvUpdate, dvDelete } from '../api/dataverseClient';
import { Task, Collection, ChecklistItem } from '../types';

export async function updateCollection(
  pca: IPublicClientApplication,
  collectionId: string,
  fields: Partial<Collection>,
): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_collections', collectionId, fields as Record<string, unknown>);
}

export async function deleteCollection(
  pca: IPublicClientApplication,
  collectionId: string,
): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_collections', collectionId);
}

const TASK_FIELDS = [
  'dagrun_pm_taskid',
  'dagrun_pm_name',
  'dagrun_pm_status',
  'dagrun_pm_priority',
  'dagrun_pm_tasktype',
  'dagrun_pm_assigneeid',
  'dagrun_pm_assigneename',
  'dagrun_pm_duedate',
  'dagrun_pm_startdate',
  'dagrun_pm_cardcolor',
  'dagrun_pm_isoncriticalpath',
  'dagrun_pm_dependencyslipflag',
  'dagrun_pm_dependencyslipdays',
  'dagrun_pm_percentcomplete',
  'dagrun_pm_estimatedhours',
  'dagrun_pm_effortcompletedhours',
  'dagrun_pm_commentcount',
  'dagrun_pm_subtaskcount',
  'dagrun_pm_subtaskcompletedcount',
  'dagrun_pm_checklistcount',
  'dagrun_pm_checklistcompletedcount',
  'dagrun_pm_attachmentcount',
  'dagrun_pm_hasdependencies',
  'dagrun_pm_hasaiactivity',
  'dagrun_pm_isrecurringinstance',
  'dagrun_pm_occurrencenumber',
  '_dagrun_pm_wipcollectionid_value',
  '_dagrun_pm_parenttaskid_value',
  '_dagrun_pm_projectid_value',
  '_dagrun_pm_phaseid_value',
  '_dagrun_pm_sprintid_value',
  'createdon',
  'dagrun_pm_description',
  'dagrun_pm_sortorder',
  'dagrun_pm_baselinestart',
  'dagrun_pm_baselineend',
  'dagrun_pm_constrainttype',
  'dagrun_pm_constraintdate',
  'dagrun_pm_hasgoals',
  'dagrun_pm_goalcount',
  'dagrun_pm_storypoints',
];

/**
 * Fetch a single task by id (all TASK_FIELDS). Used to open the Task
 * Detail Panel for a task chosen from global search or a deep link,
 * where only the id is known. Throws on 404 / access denied so callers
 * can fall back gracefully.
 */
export async function getTaskById(
  pca: IPublicClientApplication,
  taskId: string,
): Promise<Task> {
  return dvGet<Task>(pca, 'dagrun_pm_tasks', taskId, { select: TASK_FIELDS });
}

export async function getProjectTasks(
  pca: IPublicClientApplication,
  projectId: string
): Promise<Task[]> {
  return dvList<Task>(pca, 'dagrun_pm_tasks', {
    select: TASK_FIELDS,
    filter: `_dagrun_pm_projectid_value eq ${projectId} and _dagrun_pm_parenttaskid_value eq null`,
    orderby: 'dagrun_pm_sortorder asc,createdon asc',
  });
}

// ── Delta fetches for the real-time polling loop ─────────────────────
// Cursor pattern: filter on modifiedon strictly-greater-than the last
// server timestamp seen (never $skip). Callers advance their cursor off
// the returned modifiedon values so client clock skew never accumulates.

export type TaskDelta = Task & { modifiedon?: string };

export async function getProjectTaskDeltas(
  pca: IPublicClientApplication,
  projectId: string,
  sinceIso: string,
): Promise<TaskDelta[]> {
  return dvList<TaskDelta>(pca, 'dagrun_pm_tasks', {
    select: [...TASK_FIELDS, 'modifiedon'],
    filter: `_dagrun_pm_projectid_value eq ${projectId} and _dagrun_pm_parenttaskid_value eq null and modifiedon gt ${sinceIso}`,
    orderby: 'modifiedon asc',
  });
}

export type CollectionDelta = Collection & { modifiedon?: string };

export async function getProjectCollectionDeltas(
  pca: IPublicClientApplication,
  projectId: string,
  sinceIso: string,
): Promise<CollectionDelta[]> {
  return dvList<CollectionDelta>(pca, 'dagrun_pm_collections', {
    select: [
      'dagrun_pm_collectionid',
      'dagrun_pm_name',
      'dagrun_pm_sortorder',
      'dagrun_pm_isdonecollection',
      'dagrun_pm_wiplimit',
      'dagrun_pm_color',
      'dagrun_pm_ishidden',
      '_dagrun_pm_viewid_value',
      '_dagrun_pm_projectid_value',
      'modifiedon',
    ],
    filter: `_dagrun_pm_projectid_value eq ${projectId} and _dagrun_pm_viewid_value eq null and dagrun_pm_ishidden eq false and modifiedon gt ${sinceIso}`,
    orderby: 'modifiedon asc',
  });
}

export async function getSubtasks(
  pca: IPublicClientApplication,
  parentTaskId: string
): Promise<Task[]> {
  return dvList<Task>(pca, 'dagrun_pm_tasks', {
    select: TASK_FIELDS,
    filter: `_dagrun_pm_parenttaskid_value eq ${parentTaskId}`,
    orderby: 'createdon asc',
  });
}

export async function getProjectCollections(
  pca: IPublicClientApplication,
  projectId: string
): Promise<Collection[]> {
  return dvList<Collection>(pca, 'dagrun_pm_collections', {
    select: [
      'dagrun_pm_collectionid',
      'dagrun_pm_name',
      'dagrun_pm_sortorder',
      'dagrun_pm_isdonecollection',
      'dagrun_pm_wiplimit',
      'dagrun_pm_color',
      'dagrun_pm_ishidden',
      '_dagrun_pm_viewid_value',
      '_dagrun_pm_projectid_value',
    ],
    filter: `_dagrun_pm_projectid_value eq ${projectId} and _dagrun_pm_viewid_value eq null and dagrun_pm_ishidden eq false`,
    orderby: 'dagrun_pm_sortorder asc',
  });
}

export async function createCollection(
  pca: IPublicClientApplication,
  projectId: string,
  name: string,
  sortOrder: number,
  color?: string | null,
): Promise<string> {
  const data: Record<string, unknown> = {
    dagrun_pm_name: name,
    dagrun_pm_sortorder: sortOrder,
    dagrun_pm_isdonecollection: false,
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
  };
  if (color) data.dagrun_pm_color = color;
  return dvCreate(pca, 'dagrun_pm_collections', data);
}

export async function updateTaskStatus(
  pca: IPublicClientApplication,
  taskId: string,
  status: number
): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_tasks', taskId, { dagrun_pm_status: status });
}

export async function updateTaskStoryPoints(
  pca: IPublicClientApplication,
  taskId: string,
  points: number | null
): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_tasks', taskId, { dagrun_pm_storypoints: points });
}

export async function updateTaskCollection(
  pca: IPublicClientApplication,
  taskId: string,
  collectionId: string | null
): Promise<void> {
  const data: Record<string, unknown> = collectionId
    ? { 'dagrun_pm_wipcollectionid@odata.bind': `/dagrun_pm_collections(${collectionId})` }
    : { 'dagrun_pm_wipcollectionid@odata.bind': null };
  return dvUpdate(pca, 'dagrun_pm_tasks', taskId, data);
}

export async function updateTaskSortOrder(
  pca: IPublicClientApplication,
  taskId: string,
  sortOrder: number,
): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_tasks', taskId, { dagrun_pm_sortorder: sortOrder });
}

export interface CreateTaskInput {
  title: string;
  collectionId?: string;
  priority?: number;
  status?: number;
  taskType?: number;   // TaskType — omitted = Dataverse column default (Task)
  assigneeId?: string | null;
  assigneeName?: string | null;
  dueDate?: string | null;
  startDate?: string | null;
  estimatedHours?: number | null;
  description?: string | null;
  sortOrder?: number;
}

export async function createTask(
  pca: IPublicClientApplication,
  projectId: string,
  input: string | CreateTaskInput,
  collectionId?: string
): Promise<string> {
  // Back-compat: (pca, projectId, title: string, collectionId?) still works.
  const inp: CreateTaskInput = typeof input === 'string'
    ? { title: input, collectionId }
    : input;

  const data: Record<string, unknown> = {
    dagrun_pm_name: inp.title,
    dagrun_pm_status: inp.status ?? 100000000,
    dagrun_pm_percentcomplete: 0,
    dagrun_pm_effortcompletedhours: 0,
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
  };
  if (inp.priority != null) data.dagrun_pm_priority = inp.priority;
  if (inp.taskType != null) data.dagrun_pm_tasktype = inp.taskType;
  if (inp.collectionId) {
    data['dagrun_pm_wipcollectionid@odata.bind'] = `/dagrun_pm_collections(${inp.collectionId})`;
  }
  if (inp.assigneeId) data.dagrun_pm_assigneeid = inp.assigneeId;
  if (inp.assigneeName) data.dagrun_pm_assigneename = inp.assigneeName;
  if (inp.dueDate) data.dagrun_pm_duedate = inp.dueDate;
  if (inp.startDate) data.dagrun_pm_startdate = inp.startDate;
  if (inp.estimatedHours != null) data.dagrun_pm_estimatedhours = inp.estimatedHours;
  if (inp.description) data.dagrun_pm_description = inp.description;
  if (inp.sortOrder != null) data.dagrun_pm_sortorder = inp.sortOrder;
  const taskId = await dvCreate(pca, 'dagrun_pm_tasks', data);

  // Fire-and-forget Created activity log entry. Imported inline to avoid
  // a circular dep with activityService which doesn't depend on taskService.
  try {
    const { logActivity, ActivityEntityType } = await import('./activityService');
    await logActivity(pca, {
      projectId,
      entityType: ActivityEntityType.Task,
      entityId: taskId,
      entityTitle: inp.title,
      action: 'Created',
    });
  } catch { /* non-fatal */ }

  return taskId;
}

export async function createSubtask(
  pca: IPublicClientApplication,
  projectId: string,
  parentTaskId: string,
  title: string,
  collectionId?: string | null,
): Promise<string> {
  const data: Record<string, unknown> = {
    dagrun_pm_name: title,
    dagrun_pm_status: 100000000,
    dagrun_pm_percentcomplete: 0,
    dagrun_pm_effortcompletedhours: 0,
    dagrun_pm_tasktype: 100000001, // Subtask
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
    'dagrun_pm_parenttaskid@odata.bind': `/dagrun_pm_tasks(${parentTaskId})`,
  };
  if (collectionId) {
    data['dagrun_pm_wipcollectionid@odata.bind'] = `/dagrun_pm_collections(${collectionId})`;
  }
  return dvCreate(pca, 'dagrun_pm_tasks', data);
}

/**
 * Delete a task. Child subtasks are removed first (best-effort) so a parent
 * with subtasks doesn't fail on a RemoveLink constraint; then the task row
 * itself is deleted. A Deleted activity entry is logged fire-and-forget.
 * Throws if the final task delete fails so callers can surface the error and
 * keep the row visible.
 */
export async function deleteTask(
  pca: IPublicClientApplication,
  task: Pick<Task, 'dagrun_pm_taskid' | 'dagrun_pm_name' | '_dagrun_pm_projectid_value'>,
): Promise<void> {
  // Remove child subtasks first (best-effort — a failure here shouldn't block
  // the primary delete; Dataverse will still error below if a hard FK remains).
  try {
    const subs = await getSubtasks(pca, task.dagrun_pm_taskid);
    await Promise.all(subs.map(s => dvDelete(pca, 'dagrun_pm_tasks', s.dagrun_pm_taskid).catch(() => undefined)));
  } catch { /* best-effort */ }

  await dvDelete(pca, 'dagrun_pm_tasks', task.dagrun_pm_taskid);

  try {
    const projectId = task._dagrun_pm_projectid_value;
    if (projectId) {
      const { logActivity, ActivityEntityType } = await import('./activityService');
      await logActivity(pca, {
        projectId,
        entityType: ActivityEntityType.Task,
        entityId: task.dagrun_pm_taskid,
        entityTitle: task.dagrun_pm_name,
        action: 'Deleted',
      });
    }
  } catch { /* non-fatal */ }
}

// ── Checklist ─────────────────────────────────────────────────────────

const CHECKLIST_FIELDS = [
  'dagrun_pm_checklistitemid',
  'dagrun_pm_name',
  'dagrun_pm_iscompleted',
  'dagrun_pm_sortorder',
  'dagrun_pm_completedon',
  'dagrun_pm_completedby',
  '_dagrun_pm_taskid_value',
  'createdon',
];

export async function getChecklistItems(
  pca: IPublicClientApplication,
  taskId: string,
): Promise<ChecklistItem[]> {
  return dvList<ChecklistItem>(pca, 'dagrun_pm_checklistitems', {
    select: CHECKLIST_FIELDS,
    filter: `_dagrun_pm_taskid_value eq ${taskId}`,
    orderby: 'dagrun_pm_sortorder asc,createdon asc',
  });
}

export async function createChecklistItem(
  pca: IPublicClientApplication,
  taskId: string,
  text: string,
  sortOrder: number,
): Promise<string> {
  return dvCreate(pca, 'dagrun_pm_checklistitems', {
    dagrun_pm_name: text,
    dagrun_pm_iscompleted: false,
    dagrun_pm_sortorder: sortOrder,
    'dagrun_pm_taskid@odata.bind': `/dagrun_pm_tasks(${taskId})`,
  });
}

export async function updateChecklistItem(
  pca: IPublicClientApplication,
  itemId: string,
  fields: Partial<ChecklistItem>,
): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_checklistitems', itemId, fields as Record<string, unknown>);
}

export async function deleteChecklistItem(
  pca: IPublicClientApplication,
  itemId: string,
): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_checklistitems', itemId);
}
